import { useCallback, useRef, useState } from 'react';

import type { ConfirmationDecision } from './ConfirmationModal';

export interface ResolveConfirmationRequest {
  sessionID: string;
  toolUseID: string;
  decision: ConfirmationDecision;
}

export interface ResolveConfirmationResult {
  /** `true` when the POST completed (200 OR 404). */
  ok: boolean;
  /** HTTP status code from the server. */
  status: number;
}

export interface UseResolveConfirmationOptions {
  /**
   * Issues `POST /confirm` with the user's decision. Resolves with the
   * raw status code; the hook handles 404 (already-resolved) as
   * idempotent success.
   */
  postConfirm: (
    request: ResolveConfirmationRequest,
  ) => Promise<{ status: number }>;
  /** Fires after a successful resolve so the parent can clear local state. */
  onResolved?: (request: ResolveConfirmationRequest) => void;
  /** Fires on network errors and non-2xx/404 responses. */
  onError?: (error: Error) => void;
}

export interface UseResolveConfirmationResult {
  isResolving: boolean;
  resolve: (
    request: ResolveConfirmationRequest,
  ) => Promise<ResolveConfirmationResult | null>;
}

/**
 * Resolves a tool-use confirmation by POSTing `/confirm`.
 *
 * Idempotency model: the orchestrator returns 404 when the same
 * `toolUseID` has already been resolved (e.g., a race between the
 * user's first tap and a network retry). We treat 404 as success so
 * the UI doesn't surface a confusing error to a user who just
 * legitimately confirmed.
 *
 * The hook also guards re-entry: while a resolve is in flight, extra
 * calls are no-ops (return null). Combined with the modal's
 * `isResolving` button-disable, this prevents the same decision from
 * being submitted twice over the wire.
 */
export function useResolveConfirmation(
  options: UseResolveConfirmationOptions,
): UseResolveConfirmationResult {
  const { postConfirm, onResolved, onError } = options;

  const [isResolving, setIsResolving] = useState(false);
  const inFlightRef = useRef(false);

  const resolve = useCallback(
    async (
      request: ResolveConfirmationRequest,
    ): Promise<ResolveConfirmationResult | null> => {
      if (inFlightRef.current) return null;
      inFlightRef.current = true;
      setIsResolving(true);

      try {
        const { status } = await postConfirm(request);
        const ok = (status >= 200 && status < 300) || status === 404;

        if (!ok) {
          onError?.(new Error(`POST /confirm returned HTTP ${status}`));

          return { ok: false, status };
        }

        onResolved?.(request);

        return { ok: true, status };
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));

        return null;
      } finally {
        inFlightRef.current = false;
        setIsResolving(false);
      }
    },
    [postConfirm, onResolved, onError],
  );

  return { isResolving, resolve };
}
